/**
 * CHECK IN / OUT — the primary presence control.
 *
 * Face recognition is Phase 8 and, by design, is an ARRIVAL sensor that may
 * never emit `out` (docs/01 §2). This screen is therefore not a fallback that
 * gets built once and forgotten: it is the only way anybody ever checks out,
 * and it stays that way after the camera ships. Hence one enormous, unambiguous
 * button that says what it will do and what state you are in afterwards.
 *
 * "Last seen" is the passive fix for the forgotten checkout (docs/01 §2). There
 * is deliberately no "are you still here?" prompt — someone shut in their own
 * room for six hours is a normal state here, and a stale entry is made
 * self-evident instead of interrupting them. Stale is AMBER, never red: it is
 * an observation, not an accusation.
 */

import {
  SpaceEmpty,
  SpaceStyles,
  formatAgo,
  formatDuration,
  initialsOf,
  isStale,
  mutationCtx,
  presenceAt,
  timeOfDay,
  useAction,
  useNow,
  useSpace,
  type PresenceEntry,
  type SpacePerson,
  type SpaceScreenProps,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'

export interface CheckInProps extends SpaceScreenProps {
  /** How often the "last seen" ages tick. 0 freezes it — tests pass 0. */
  readonly tickMs?: number
}

export function CheckIn(props: CheckInProps) {
  const { db, clock, actorId } = props
  const now = useNow(clock, props.tickMs ?? 30_000)
  const space = useSpace(db)
  const run = useAction()

  const presence = presenceAt(space.presenceEvents, now, space.settings.dayRolloverHour)
  const me = presence.get(actorId as SpacePerson['id']) ?? null
  const iAmIn = me?.present === true

  const withPresence = space.people.map(person => ({
    person,
    entry: presence.get(person.id) ?? null,
  }))
  const here = withPresence.filter(r => r.entry?.present === true)
  const away = withPresence.filter(r => r.entry?.present !== true && r.person.kind === 'member')
  const others = here.filter(r => String(r.person.id) !== actorId)

  const toggle = () => {
    const ctx = mutationCtx(props)
    run(() =>
      iAmIn ? mutations.checkOut(ctx, actorId, 'app') : mutations.checkIn(ctx, actorId, 'app'),
    )
  }

  // The sub-line is the only place the session length appears, and an aria-label
  // on the button REPLACES its contents for a screen reader. Naming the button
  // with both keeps "in since 10:48 · 3h 12m" audible instead of silently
  // sighted-only.
  const detail =
    iAmIn && me?.since !== null && me?.since !== undefined
      ? `In since ${timeOfDay(me.since)} · ${formatDuration(now - me.since)}`
      : lastSeenLine(me, now)

  return (
    <section className="sp" aria-labelledby="sp-presence-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-presence-h">
          Presence
        </h2>
        <span className="sp-note">{here.length} in the office</span>
      </div>

      <button
        type="button"
        className="sp-presence-btn"
        data-present={iAmIn ? 'true' : 'false'}
        aria-label={`${iAmIn ? 'Check out' : 'Check in'} — ${detail}`}
        onClick={toggle}
      >
        <span>{iAmIn ? 'Check out' : 'Check in'}</span>
        <span className="sp-presence-sub">{detail}</span>
      </button>

      <p aria-live="polite" className="sp-note" data-testid="presence-status">
        {iAmIn ? "You're checked in." : "You're not checked in."}
      </p>

      <div className="card">
        <div className="sp-head">
          <h3 className="sp-card-title">
            In the office
          </h3>
          <span className="sp-note">{here.length}</span>
        </div>
        {others.length === 0 && !iAmIn ? (
          <SpaceEmpty mark="🏢" hint="The camera only ever says someone arrived; checking out is always a tap, here or on the wall tablet.">
            Nobody is checked in.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list" aria-label="People in the office">
            {here.map(({ person, entry }) => (
              <li key={person.id} className="sp-person">
                <span className="sp-avatar" data-present="true" aria-hidden="true">
                  {initialsOf(person.displayName)}
                </span>
                <span className="grow">
                  <span className="sp-person-name">
                    {person.displayName}
                    {String(person.id) === actorId ? ' (you)' : ''}
                  </span>
                  <span className="sp-person-meta">
                    {entry?.since !== null && entry?.since !== undefined
                      ? `Since ${timeOfDay(entry.since)} · ${formatDuration(now - entry.since)}`
                      : 'In'}
                  </span>
                </span>
                {entry !== null && isStale(entry, now) ? (
                  <span className="pill pill-warn">
                    last seen {formatAgo(entry.lastSeen, now)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {away.length > 0 ? (
        <div className="card">
          <h3 className="sp-card-title">
            Not in
          </h3>
          <ul className="sp-list" aria-label="People not in the office">
            {away.map(({ person, entry }) => (
              <li key={person.id} className="sp-person">
                <span className="sp-avatar" data-present="false" aria-hidden="true">
                  {initialsOf(person.displayName)}
                </span>
                <span className="grow">
                  <span className="sp-person-name">{person.displayName}</span>
                  <span className="sp-person-meta">
                    {lastSeenLine(entry, now)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

function lastSeenLine(entry: PresenceEntry | null, now: number): string {
  if (entry === null || entry.lastSeen === null) return 'Not seen today'
  return `Last seen ${formatAgo(entry.lastSeen, now)}`
}
